import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Table, Typography, Modal, message, Space, Tag } from 'antd';
import {
  DeleteTwoTone,
  ExclamationCircleOutlined,
  EditTwoTone,
  HeartOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { Breakpoint } from 'antd/lib/_util/responsiveObserve';

import * as actions from '../../actions/publicationsActions';
import { RootState } from '../../reducers/store';
import { filterApiResponse } from './utils';
import EditPublicationForm from './Form/Message/Edit';
import EditEventForm from './Form/Event/Edit';
import { ReadPollForm } from './Form/Poll/Read';
import { PublicationTargetting } from 'src/ui/PublicationTargetting/PublicationTargetting';

import { ApiPublicationType, PublicationListPropsType } from './types';
import { PUBLICATIONS_TYPES } from 'src/constants';

const { Text, Link } = Typography;

export const PUBLICATIONS_PER_PAGE = 20;
const { confirm } = Modal;

const PublicationsList: React.FC<PublicationListPropsType> = ({
  publications,
  listPublications,
  publicationsTotal,
  deletePublication,
  filterSearch,
  filterType,
}) => {
  const [current, setCurrent] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<{
    [key: string]: boolean;
  }>(
    publications.reduce((popoverOpenStatus, publication) => {
      return {
        ...popoverOpenStatus,
        [publication.id]: false,
      };
    }, {})
  );

  const confirmDeletion = (id: string, model_type: string) => {
    confirm({
      title: 'Supprimer cette publication ?',
      icon: <ExclamationCircleOutlined />,
      content: 'Êtes-vous sûr(e) de vouloir supprimer cette publication ?',
      okText: 'Supprimer',
      cancelText: 'Annuler',
      onOk() {
        deletePublication(
          id,
          model_type,
          () => {
            message.success('La publication a bien été supprimée');
            setTotal(total - 1);
          },
          () => {
            message.error(
              "Une erreur s'est produite lors de la suppression de la publication"
            );
          }
        );
        return Promise.resolve();
      },
    });
  };

  const renderEditModal = (id: string, modelType: string) => (
    <Modal
      open={isEditModalOpen[id]}
      destroyOnClose={true}
      onOk={() => setIsEditModalOpen({ ...isEditModalOpen, [id]: true })}
      onCancel={() => setIsEditModalOpen({ ...isEditModalOpen, [id]: false })}
      footer={null}
    >
      {isEditModalOpen[id] && renderEditForm(id, modelType)}
    </Modal>
  );

  const renderEditForm = (id: string, modelType: string) => {
    switch (modelType) {
      case 'information':
        return (
          <EditPublicationForm
            publicationId={id}
            onSuccess={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
            closeModal={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
          />
        );
      case 'event':
        return (
          <EditEventForm
            eventId={id}
            onSuccess={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
            closeModal={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
          />
        );
      case 'poll':
        return <ReadPollForm id={id} />;
      default:
        return (
          <EditPublicationForm
            publicationId={id}
            onSuccess={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
            closeModal={() =>
              setIsEditModalOpen({ ...isEditModalOpen, [id]: false })
            }
          />
        );
    }
  };

  const columns = [
    {
      title: 'Publications',
      dataIndex: 'publication',
      render: (publication: ApiPublicationType) => (
        <Link
          key={publication.id}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            !isEditModalOpen[publication.id] &&
              setIsEditModalOpen({
                ...isEditModalOpen,
                [publication.id]: true,
              });
          }}
        >
          <Space direction="vertical" size="small">
            {publication.title && publication.title !== 'Publication' && (
              <Text strong>{publication.title}</Text>
            )}
            <Text>{publication.text}</Text>
          </Space>
          {renderEditModal(publication.id, publication.model_type)}
        </Link>
      ),
      width: '28%',
    },
    {
      title: 'Type',
      dataIndex: 'publication',
      responsive: ['lg'] as Breakpoint[],
      render: (publication: ApiPublicationType) => (
        <Tag color={PUBLICATIONS_TYPES[publication.model_type].color}>
          {PUBLICATIONS_TYPES[publication.model_type].label}
        </Tag>
      ),
      width: '5%',
    },
    {
      title: 'Ciblage',
      dataIndex: 'publication',
      //responsive: ['lg'] as Breakpoint[],
      render: ({
        targetDistricts,
        targetTowns,
        targetDepartments,
        diffusions,
        isPublic,
      }: {
        targetDistricts: Array<string>;
        targetTowns: Array<string>;
        targetDepartments: Array<string>;
        diffusions: any;
        isPublic: boolean;
      }) => (
        <PublicationTargetting
          targetDistricts={targetDistricts}
          targetTowns={targetTowns}
          targetDepartments={targetDepartments}
          diffusions={diffusions}
          isPublic={isPublic}
        />
      ),
      width: '5%',
    },
    {
      title: 'Likes / Commentaires',
      dataIndex: 'publication',
      responsive: ['lg'] as Breakpoint[],
      render: (publication: ApiPublicationType) => (
        <Space>
          <div>
            {publication.like_count} <HeartOutlined />
            {' / '}
          </div>
          <div>
            {publication.comment_count} <CommentOutlined />
          </div>
        </Space>
      ),
      width: '10%',
    },
    {
      title: 'Auteur',
      dataIndex: 'author',
      responsive: ['lg'] as Breakpoint[],
      render: (author: string) => <Text>{author}</Text>,
      width: '10%',
    },
    {
      title: 'Créé il y a',
      dataIndex: 'createdAt',
      responsive: ['lg'] as Breakpoint[],
      render: (createdAt: string) => <Text>{createdAt}</Text>,
      width: '5%',
    },
    {
      title: '',
      dataIndex: 'publication',
      render: (publication: ApiPublicationType) => (
        <Space size="large">
          <div style={{ width: '10px' }}>
            {publication.model_type !== 'poll' && (
              <EditTwoTone
                id={`detailButton${publication.id}`}
                style={{ fontSize: '20px' }}
                onClick={() => {
                  !isEditModalOpen[publication.id] &&
                    setIsEditModalOpen({
                      ...isEditModalOpen,
                      [publication.id]: true,
                    });
                }}
              />
            )}
          </div>
          <DeleteTwoTone
            id={`deleteButton${publication.id}`}
            style={{ fontSize: '20px' }}
            twoToneColor="red"
            onClick={() => {
              confirmDeletion(publication.id, publication.model_type);
            }}
          />
        </Space>
      ),
      width: '5%',
    },
  ];

  useEffect(() => {
    setLoading(true);
    listPublications(current, filterType, filterSearch);
  }, [listPublications, current, filterType, filterSearch]);

  useEffect(() => {
    setLoading(false);
    setTotal(publicationsTotal);
  }, [publications, publicationsTotal]);

  /* Change current page if we no longer have data on it */
  useEffect(
    () => {
      current !== 1 &&
        total % PUBLICATIONS_PER_PAGE === 0 &&
        setCurrent(current - 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total]
  );

  return (
    <Table
      title={() => `Liste de vos publications (${total})`}
      columns={columns}
      dataSource={publications}
      size="middle"
      tableLayout="fixed"
      loading={loading}
      pagination={{
        current: current,
        total: total,
        pageSize: PUBLICATIONS_PER_PAGE,
        showSizeChanger: false,
        position: ['bottomCenter'],
      }}
      onChange={(pagination: any) => setCurrent(pagination.current)}
    ></Table>
  );
};

const mapStateToProps = ({ publications }: RootState) => ({
  publications: filterApiResponse(publications.items),
  publicationsTotal: publications.total,
});

export default connect(mapStateToProps, actions)(PublicationsList);
